import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { ShoppingBag, Eye, EyeOff } from 'lucide-react';

export const CustomerLogin = () => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useCustomerAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(loginId, password);
      toast.success('ログインしました');
      navigate('/customer/catalog');
    } catch (error) {
      const message = error.response?.data?.detail || 'ログインに失敗しました';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="customer-login-page">
      {/* Form Section */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
              <ShoppingBag className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              オンライン注文
            </CardTitle>
            <CardDescription className="text-slate-500">
              お客様ログイン
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="loginId" className="text-slate-700">ログインID</Label>
                <Input
                  id="loginId"
                  type="text"
                  placeholder="ログインID"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  required
                  className="border-slate-200 focus:ring-blue-600"
                  data-testid="customer-login-id-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">パスワード</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-slate-200 focus:ring-blue-600 pr-10"
                    data-testid="customer-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="customer-submit-btn"
              >
                {loading ? '処理中...' : 'ログイン'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-500">
              <p>ログイン情報がわからない場合は、</p>
              <p>お取引先の管理者にお問い合わせください。</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Section */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/264636/pexels-photo-264636.jpeg)',
        }}
        data-testid="customer-login-image"
      >
        <div className="h-full w-full bg-blue-900/70 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-4">オンライン注文システム</h2>
            <p className="text-lg text-blue-100 max-w-md">
              いつでもどこでも簡単に注文。
              商品カタログから必要な商品を選んで、
              まとめて発注できます。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
